import { camelize } from 'casing'
import { isEmpty } from 'lodash'
import type { AxiosError } from 'axios'
import type { Dispatch } from 'redux'

import { getPartnerDataFromIdentityService } from '../../api/users'
import type { AccountData } from '../../types'
import { PartnerType } from '../../types/partner'
import { actionTypeWrapper } from '../../utils/actionTypeWrapper'
import { errorLogger } from '../../utils/errorLogger'
import { getErrorMessages } from '../../utils/error'
import { RequestFailureMessage } from '../../utils/messagesContants'
import {
  getCachedPartnerData,
  setCachedPartnerData,
} from '../../utils/partnerDataCache'
import { startLoading, stopLoading } from '../loading/actions'
import { LoadingTypes } from '../loading/reducer'
import {
  normalizeSubscribedProducts,
  type SalesSubscription,
} from './normalizeSubscribedProducts'

const SET_PARTNER = 'SET_PARTNER'
const GET_PARTNER_FAILED = 'GET_PARTNER_FAILED'
const SET_CURRENT_TENANT_ID = 'SET_CURRENT_TENANT_ID'
const SET_AVAILABLE_TENANTS = 'SET_AVAILABLE_TENANTS'

type PartnerAccountSlice = {
  isLoading: boolean
  partnerData?: { partnerId?: string } | null
}

type GetAccountState = {
  AccountData: Record<PartnerType, PartnerAccountSlice>
}

type SubscribedProducts = {
  SALES?: SalesSubscription
  [key: string]: unknown
}

export const setAccount = (data: Partial<AccountData>) => {
  const camelized = camelize(data) as Partial<AccountData>
  return {
    type: SET_PARTNER,
    payload: {
      ...camelized,
      subscribedProducts: normalizeSubscribedProducts(
        camelized.subscribedProducts as SubscribedProducts | undefined
      ),
    },
  }
}

export const getPartnerFailed = (err: unknown) => ({
  type: GET_PARTNER_FAILED,
  payload: err,
})

export const getAccount =
  (partnerId: string, partnerObjectType: PartnerType = PartnerType.User) =>
  async (dispatch: Dispatch, getState: () => GetAccountState) => {
    const partnerState = getState().AccountData[partnerObjectType]
    if (partnerState.isLoading) return
    if (
      !isEmpty(partnerState.partnerData) &&
      partnerState.partnerData?.partnerId === partnerId
    )
      return

    dispatch(startLoading(LoadingTypes.PARTNER_DATA))

    try {
      const cachedData = await getCachedPartnerData(partnerId)

      if (cachedData) {
        await dispatch(
          actionTypeWrapper(partnerObjectType, setAccount(cachedData))
        )

        if (partnerObjectType !== PartnerType.Admin) {
          dispatch({
            type: SET_CURRENT_TENANT_ID,
            payload: partnerId,
          })
          dispatch({
            type: SET_AVAILABLE_TENANTS,
            payload: [cachedData],
          })
        }

        dispatch(stopLoading(LoadingTypes.PARTNER_DATA))

        setTimeout(() => {
          getPartnerDataFromIdentityService(
            partnerId,
            partnerObjectType === PartnerType.Admin
          )
            .then(({ data }) => {
              const normalizedData = {
                ...data,
                websiteUrl: data.company_website,
                industry: data.company_industry,
              }
              setCachedPartnerData(partnerId, normalizedData)
              dispatch(
                actionTypeWrapper(partnerObjectType, setAccount(normalizedData))
              )
              if (partnerObjectType !== PartnerType.Admin) {
                dispatch({
                  type: SET_AVAILABLE_TENANTS,
                  payload: [normalizedData],
                })
              }
            })
            .catch(bgError => {
              console.warn('[PartnerCache] Background refresh failed:', bgError)
            })
        }, 10000)

        return
      }

      const { data } = await getPartnerDataFromIdentityService(
        partnerId,
        partnerObjectType === PartnerType.Admin
      )

      const normalizedData = {
        ...data,
        websiteUrl: data.company_website,
        industry: data.company_industry,
      }

      await setCachedPartnerData(partnerId, normalizedData)

      await dispatch(
        actionTypeWrapper(partnerObjectType, setAccount(normalizedData))
      )

      if (partnerObjectType !== PartnerType.Admin) {
        dispatch({
          type: SET_CURRENT_TENANT_ID,
          payload: partnerId,
        })
        dispatch({
          type: SET_AVAILABLE_TENANTS,
          payload: [normalizedData],
        })
      }
    } catch (err: unknown) {
      const errMess = getErrorMessages([RequestFailureMessage])(
        (err as AxiosError<ErrorResponse>).response
      )
      await dispatch(
        actionTypeWrapper(partnerObjectType, getPartnerFailed(errMess))
      )
      const globalState = getState()
      errorLogger({ globalState })(err as Error)
    } finally {
      dispatch(stopLoading(LoadingTypes.PARTNER_DATA))
    }
  }
